import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Package, Printer, Truck, Copy, CheckCircle2, RotateCcw, MessageCircle,
  Send, Megaphone, Star, Bell, Trash2, Users, Tag, Shield, Download,
  TrendingUp, AlertTriangle, ShoppingBag, Clock, Plus
} from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pp-owner-token";
const ORDERS_KEY = process.env.REACT_APP_ORDERS_KEY;

const auth = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    params: { key: ORDERS_KEY },
  };
};

const labelBadge = (status) => {
  if (status === "created" || status === "digital_delivery") return "bg-[#39ff14] text-black";
  if (status === "label_error") return "bg-red-500/20 text-red-400 border border-red-500/40";
  return "bg-[#f5b700]/15 text-[#f5b700] border border-[#f5b700]/40";
};
const labelText = (status) => {
  if (status === "created") return "Label Ready";
  if (status === "digital_delivery") return "Digital Delivery";
  if (status === "label_error") return "Label Error";
  return "Label Queued";
};
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtErr = (d) => (d == null ? "Something went wrong" : typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg || JSON.stringify(e)).join(" ") : d.msg || String(d));

/* ---------- Stat tile ---------- */
const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#121316] border border-[#22242a] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon size={16} className={accent || "text-[#39ff14]"} strokeWidth={2} />
    <p className="f-disp text-2xl text-white tabular-nums mt-3">{value}</p>
    <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mt-1">{label}</p>
  </div>
);

/* ---------- Sales analytics chart ---------- */
const AnalyticsCard = ({ data }) => {
  const [period, setPeriod] = useState("daily");
  const daily = data.daily_series || [];
  const weekly = (data.weekly || []).slice(-8);
  const monthly = (data.monthly || []).slice(-6);
  const series = period === "daily" ? daily.map((d) => ({ label: d.day.slice(5), revenue_cents: d.revenue_cents, key: d.day }))
    : period === "weekly" ? weekly.map((w) => ({ label: w.period.split("-W")[1] ? `W${w.period.split("-W")[1]}` : w.period, revenue_cents: w.revenue_cents, key: w.period }))
    : monthly.map((m) => ({ label: m.period, revenue_cents: m.revenue_cents, key: m.period }));
  const max = Math.max(1, ...series.map((s) => s.revenue_cents));
  const periodTotal = series.reduce((s, x) => s + x.revenue_cents, 0);
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="analytics-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2">
          <TrendingUp size={18} className="text-[#39ff14]" /> Sales Analytics
        </h2>
        <div className="flex gap-1 bg-[#0a0a0c] border border-[#22242a] rounded-lg p-1" data-testid="chart-period-toggle">
          {["daily", "weekly", "monthly"].map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
              className={`f-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md transition-colors ${period === p ? "bg-[#39ff14] text-black font-bold" : "text-[#8e929b] hover:text-white"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <p className="f-body text-xs text-[#8e929b] mb-5">
        {period === "daily" ? "Last 14 days" : period === "weekly" ? "Last 8 weeks" : "Last 6 months"} · <span className="text-[#39ff14] f-mono">{money(periodTotal)}</span> in view
      </p>
      <div className="flex items-end gap-1.5 sm:gap-2 h-52 sm:h-64 px-1 overflow-hidden">
        {series.map((s) => (
          <div key={s.key} className="flex-1 flex flex-col items-center gap-1.5 group min-w-0 h-full justify-end">
            <span className="f-mono text-[9px] text-[#ecebe4] opacity-0 group-hover:opacity-100 transition-opacity tabular-nums whitespace-nowrap">{money(s.revenue_cents)}</span>
            <div
              className="w-full bg-[#39ff14]/80 group-hover:bg-[#39ff14] group-hover:shadow-[0_0_16px_rgba(57,255,20,0.4)] transition-all rounded-t"
              style={{ height: `${Math.max(2, (s.revenue_cents / max) * 100)}%` }}
              title={`${s.key}: ${money(s.revenue_cents)}`}
            />
            <span className="f-mono text-[9px] sm:text-[10px] text-[#8e929b] truncate w-full text-center">{s.label}</span>
          </div>
        ))}
        {series.length === 0 && <p className="f-body text-xs text-[#8e929b] mx-auto self-center">No sales in this view yet.</p>}
      </div>
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div>
          <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest">This week</p>
          <p className="f-disp text-lg text-white">{money((data.weekly || []).slice(-1)[0]?.revenue_cents)}</p>
        </div>
        <div>
          <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest">This month</p>
          <p className="f-disp text-lg text-white">{money((data.monthly || []).slice(-1)[0]?.revenue_cents)}</p>
        </div>
        <div>
          <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest">Best seller</p>
          <p className="f-body text-xs text-white truncate">{(data.best_sellers || [])[0]?.name || "—"}</p>
        </div>
      </div>
      {(data.best_sellers || []).length > 0 && (
        <div className="mt-5 space-y-2">
          {data.best_sellers.map((b) => (
            <div key={b.name} className="flex justify-between items-center bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2">
              <span className="f-body text-xs text-white truncate">{b.name}</span>
              <span className="f-mono text-[10px] text-[#39ff14] shrink-0 ml-3">{b.qty} sold · {money(b.revenue_cents)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Customer database ---------- */
const CustomersCard = () => {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");
  useEffect(() => {
    axios.get(`${API}/customers`, auth()).then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  }, []);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? customers.filter((c) => c.email.toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q))
    : customers;
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="customers-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Users size={18} className="text-[#39ff14]" /> Customers
        <span className="f-mono text-[10px] font-bold px-2 py-1 bg-[#22242a] text-[#8e929b] uppercase">{customers.length}</span>
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Every buyer with their order history and total spent.</p>
      <input
        data-testid="customer-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search name or email…"
        className="w-full bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2.5 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] mb-4"
      />
      {filtered.length === 0 && <p className="f-body text-xs text-[#8e929b]">{q ? `No customers match "${query}"` : "No customers yet."}</p>}
      <div className="space-y-2">
        {filtered.map((c) => (
          <div key={c.email} className="bg-[#0a0a0c] border border-[#22242a] rounded-lg overflow-hidden">
            <button
              data-testid={`customer-${c.email}`}
              onClick={() => setOpen(open === c.email ? null : c.email)}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#121316] transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-[#39ff14]/15 border border-[#39ff14]/40 text-[#39ff14] f-disp text-sm flex items-center justify-center shrink-0 uppercase">
                {(c.name || c.email || "?")[0]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="f-body text-xs text-white truncate">{c.name || c.email}</p>
                <p className="f-mono text-[9px] text-[#8e929b] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
              </div>
              <span className="f-mono text-[10px] text-[#39ff14] shrink-0 text-right">{c.paid_orders} orders<br />{money(c.spent_cents)}</span>
            </button>
            {open === c.email && (
              <div className="px-4 pb-3 border-t border-[#22242a] pt-3 space-y-2">
                <div className="flex gap-2 mb-1">
                  <button
                    data-testid={`customer-copy-${c.email}`}
                    onClick={() => { navigator.clipboard.writeText(c.email); toast.success("Email copied"); }}
                    className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 bg-[#22242a] text-white rounded-md hover:bg-[#39ff14] hover:text-black transition-colors"
                  >
                    Copy Email
                  </button>
                  <a href={`mailto:${c.email}`}
                    className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 bg-[#22242a] text-white rounded-md hover:bg-[#39ff14] hover:text-black transition-colors">
                    Email Them
                  </a>
                </div>
                {c.order_list.map((o) => (
                  <div key={o.order_number + o.created_at} className="flex justify-between text-xs f-body bg-[#121316] border border-[#22242a] rounded-md px-3 py-2">
                    <span className="text-[#ecebe4] min-w-0 truncate">{o.order_number} · {o.items.join(", ")}</span>
                    <span className="text-[#39ff14] f-mono text-[10px] ml-3 shrink-0">{money(o.amount_total_cents)} · {o.payment_status}{o.fulfillment_status === "shipped" ? " · shipped" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Discount manager ---------- */
const CouponsCard = () => {
  const [coupons, setCoupons] = useState([]);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);
  const load = () => axios.get(`${API}/coupons`, auth()).then((r) => setCoupons(r.data.coupons || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await axios.post(`${API}/coupons`, { code, percent_off: pct }, auth());
      toast.success(`Coupon ${code.toUpperCase()} is live`);
      setCode(""); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const toggle = async (c) => {
    await axios.put(`${API}/coupons/${c.code}`, { code: c.code, percent_off: c.percent_off, active: !c.active }, auth());
    load();
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await axios.delete(`${API}/coupons/${c.code}`, auth());
    toast.success("Coupon deleted"); load();
  };
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="coupons-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Tag size={18} className="text-[#39ff14]" /> Discount Manager
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Create coupon codes buyers can use at checkout.</p>
      <div className="flex gap-2 mb-4">
        <input
          data-testid="coupon-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="CODE"
          className="flex-1 bg-[#0a0a0c] border border-[#22242a] px-3 py-2.5 text-xs f-mono uppercase text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
        />
        <div className="relative w-24 shrink-0">
          <input
            data-testid="coupon-pct-input"
            type="number" min="1" max="100" value={pct}
            onChange={(e) => setPct(Number(e.target.value))}
            className="w-full bg-[#0a0a0c] border border-[#22242a] px-3 py-2.5 pr-7 text-xs f-mono text-white focus:outline-none focus:border-[#39ff14]"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 f-mono text-xs text-[#8e929b]">%</span>
        </div>
        <button data-testid="coupon-create" onClick={create} disabled={!code.trim()}
          className="f-mono text-[10px] uppercase tracking-wider px-4 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors flex items-center gap-1">
          <Plus size={12} /> Create
        </button>
      </div>
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.code} className={`flex items-center justify-between gap-3 rounded-lg bg-[#0a0a0c] border px-3 py-2.5 ${c.active ? "border-[#39ff14]/40" : "border-[#22242a] opacity-70"}`}>
            <div className="min-w-0 flex items-center gap-2">
              <span className="f-mono text-sm font-bold text-white">{c.code}</span>
              <span className="f-mono text-[10px] font-bold px-1.5 py-0.5 bg-[#39ff14]/15 text-[#39ff14] rounded">{c.percent_off}% OFF</span>
              {c.created_at && <span className="hidden sm:block f-mono text-[9px] text-[#8e929b]">since {new Date(c.created_at).toLocaleDateString("en-US")}</span>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button data-testid={`coupon-copy-${c.code}`} onClick={() => { navigator.clipboard.writeText(c.code); toast.success(`${c.code} copied — share it with buyers`); }}
                title="Copy code"
                className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 rounded-md bg-[#22242a] text-white hover:bg-[#39ff14] hover:text-black transition-colors flex items-center gap-1">
                <Copy size={11} /> Copy
              </button>
              <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)}
                className={`f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 rounded-md transition-colors ${c.active ? "bg-[#39ff14]/15 text-[#39ff14] border border-[#39ff14]/40" : "bg-[#22242a] text-[#8e929b]"}`}>
                {c.active ? "Live" : "Off"}
              </button>
              <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)}
                className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 rounded-md bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white transition-colors">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && <p className="f-body text-xs text-[#8e929b]">No coupons yet — create one above and share the code.</p>}
      </div>
    </div>
  );
};

/* ---------- Security center + activity ---------- */
const SecurityCard = () => {
  const [activity, setActivity] = useState([]);
  const [kind, setKind] = useState("");
  const load = (k = kind) => axios.get(`${API}/activity`, { ...auth(), params: { ...auth().params, kind: k } }).then((r) => setActivity(r.data.activity || [])).catch(() => {});
  useEffect(() => { load(""); const t = setInterval(() => load(kind), 10000); return () => clearInterval(t); }, [kind]);
  const pick = (k) => { setKind(k); load(k); };
  const kindColor = (k) => k === "security" ? "text-red-400" : k === "shipping" ? "text-[#f5b700]" : k === "order" ? "text-[#39ff14]" : "text-[#8e929b]";
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="security-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Shield size={18} className="text-[#39ff14]" /> Activity Log
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Every order, refund, label and change — tracked here.</p>
      <div className="flex flex-wrap gap-1 mb-4" data-testid="activity-filters">
        {[["", "All"], ["order", "Orders"], ["shipping", "Shipping"], ["admin", "Admin"], ["security", "Security"]].map(([id, label]) => (
          <button key={id} data-testid={`activity-filter-${id || "all"}`} onClick={() => pick(id)}
            className={`f-mono text-[9px] uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition-colors ${kind === id ? "bg-[#39ff14] text-black font-bold border-[#39ff14]" : "text-[#8e929b] border-[#22242a] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1.5">
        {activity.length === 0 && <p className="f-body text-xs text-[#8e929b]">No activity yet.</p>}
        {activity.map((a, i) => (
          <div key={i} className="flex justify-between items-center text-xs bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2">
            <span className="f-body text-[#ecebe4] break-words min-w-0">
              <span className={`f-mono text-[9px] uppercase font-bold mr-2 ${kindColor(a.kind)}`}>{a.action.replace(/_/g, " ")}</span>
              {a.detail}
            </span>
            <span className="f-mono text-[9px] text-[#8e929b] shrink-0 ml-3">{a.created_at ? new Date(a.created_at).toLocaleString("en-US") : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Admin notifications ---------- */
const NotificationsCard = () => {
  const [notifs, setNotifs] = useState([]);
  const [tab, setTab] = useState("all");
  useEffect(() => {
    const load = () => axios.get(`${API}/notifications`, auth()).then((r) => setNotifs(r.data.notifications || [])).catch(() => {});
    load(); const t = setInterval(load, 10000); return () => clearInterval(t);
  }, []);
  const filtered = tab === "all" ? notifs : notifs.filter((n) => n.category === tab);
  const orderCount = notifs.filter((n) => n.category === "orders").length;
  const shipCount = notifs.filter((n) => n.category === "shipping").length;
  const icon = (n) => n.type === "failed_payment"
    ? <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
    : n.category === "shipping"
    ? <Truck size={15} className="text-[#f5b700] shrink-0 mt-0.5" />
    : <ShoppingBag size={15} className="text-[#39ff14] shrink-0 mt-0.5" />;
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="notifications-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Bell size={18} className="text-[#39ff14]" /> Notifications
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Orders and shipping, kept separate — urgent items glow red.</p>
      <div className="flex gap-1 bg-[#0a0a0c] border border-[#22242a] rounded-lg p-1 mb-4" data-testid="notif-tabs">
        {[["all", `All (${notifs.length})`], ["orders", `Orders (${orderCount})`], ["shipping", `Shipping (${shipCount})`]].map(([id, label]) => (
          <button key={id} data-testid={`notif-tab-${id}`} onClick={() => setTab(id)}
            className={`flex-1 f-mono text-[10px] uppercase tracking-wider px-2 py-2 rounded-md transition-colors ${tab === id ? "bg-[#39ff14] text-black font-bold" : "text-[#8e929b] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {filtered.length === 0 && <p className="f-body text-xs text-[#8e929b]">Nothing here yet.</p>}
        {filtered.map((n, i) => {
          const border = n.type === "failed_payment" ? "border-l-red-500" : n.category === "shipping" ? "border-l-[#f5b700]" : "border-l-[#39ff14]";
          return (
            <div key={i} className={`flex items-start gap-2.5 text-sm bg-[#0a0a0c] border border-[#22242a] border-l-4 ${border} rounded-lg px-3.5 py-2.5`}>
              {icon(n)}
              <div className="min-w-0">
                <p className="f-body text-[#ecebe4] leading-snug">{n.text}</p>
                <p className="f-mono text-[9px] text-[#8e929b] mt-1">{n.at ? new Date(n.at).toLocaleString("en-US") : ""}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- Announcement (unchanged logic, authed via key retained) ---------- */
const AnnouncementCard = () => {
  const [text, setText] = useState("");
  const [active, setActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const KEY = process.env.REACT_APP_ORDERS_KEY;
  useEffect(() => {
    axios.get(`${API}/announcement`).then((r) => { setText(r.data.text || ""); setActive(Boolean(r.data.active)); }).catch(() => {});
  }, []);
  const save = async (nextActive) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/announcement`, { text, active: nextActive }, { params: { key: KEY } });
      setActive(data.active);
      toast.success(data.active ? "Announcement is live on the store" : "Announcement off");
    } catch { toast.error("Could not save announcement"); }
    setSaving(false);
  };
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="announcement-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Megaphone size={18} className="text-[#39ff14]" /> Website Announcement
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Green bar across the top of the store.</p>
      <textarea data-testid="announcement-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={300} rows={2}
        placeholder='e.g. "SALE: 15% off all bundles this weekend only"'
        className="w-full bg-[#0a0a0c] border border-[#22242a] px-3 py-2.5 text-sm f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] resize-none mb-3" />
      <div className="flex items-center gap-3">
        <button data-testid="announcement-publish" onClick={() => save(true)} disabled={saving || !text.trim()}
          className="f-mono text-[10px] uppercase tracking-[0.14em] px-5 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors">
          {active ? "Update Live Bar" : "Publish to Store"}
        </button>
        {active && (
          <button data-testid="announcement-remove" onClick={() => save(false)} disabled={saving}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-5 py-2.5 bg-[#22242a] text-white font-bold hover:bg-red-500/20 hover:text-red-400 transition-colors">
            Take Down
          </button>
        )}
        <span className={`f-mono text-[10px] uppercase tracking-wider ${active ? "text-[#39ff14]" : "text-[#8e929b]"}`}>
          {active ? "● Live on store" : "○ Not showing"}
        </span>
      </div>
    </div>
  );
};

/* ---------- Reviews (key-retained) ---------- */
const ReviewsCard = () => {
  const [reviews, setReviews] = useState([]);
  const KEY = process.env.REACT_APP_ORDERS_KEY;
  const load = () => axios.get(`${API}/reviews/all`, { params: { key: KEY } }).then((r) => setReviews(r.data.reviews)).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    if (action === "approve") { await axios.put(`${API}/reviews/${id}/approve`, null, { params: { key: KEY } }); toast.success("Review is live on the store"); }
    else { await axios.delete(`${API}/reviews/${id}`, { params: { key: KEY } }); toast.success("Review deleted"); }
    load();
  };
  const pending = reviews.filter((r) => !r.approved);
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="reviews-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Star size={18} className="text-[#39ff14]" /> Reviews
        {pending.length > 0 && <span className="f-mono text-[10px] font-bold px-2 py-1 bg-[#f5b700] text-black uppercase">{pending.length} pending</span>}
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Approve to publish on the store.</p>
      {reviews.length === 0 && <p className="f-body text-xs text-[#8e929b]">No reviews yet.</p>}
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-[#0a0a0c] border border-[#22242a] p-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={12} className={n <= r.rating ? "text-[#39ff14] fill-[#39ff14]" : "text-[#22242a]"} strokeWidth={1.5} />
                ))}
              </div>
              <span className={`f-mono text-[9px] font-bold px-1.5 py-0.5 uppercase ${r.approved ? "bg-[#39ff14] text-black" : "bg-[#f5b700]/15 text-[#f5b700]"}`}>{r.approved ? "Live" : "Pending"}</span>
            </div>
            <p className="f-body text-xs text-[#ecebe4] mb-1">&ldquo;{r.text}&rdquo;</p>
            <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest mb-3">— {r.name} · {(r.products || []).join(", ")}</p>
            <div className="flex gap-2">
              {!r.approved && (
                <button data-testid={`review-approve-${r.id}`} onClick={() => act(r.id, "approve")}
                  className="f-mono text-[10px] uppercase tracking-[0.14em] px-3 py-1.5 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors">Approve</button>
              )}
              <button data-testid={`review-delete-${r.id}`} onClick={() => act(r.id, "delete")}
                className="f-mono text-[10px] uppercase tracking-[0.14em] px-3 py-1.5 bg-red-500/15 text-red-400 border border-red-500/40 font-bold hover:bg-red-500 hover:text-white transition-colors flex items-center gap-1">
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Stock (key-retained) ---------- */
const StockCard = () => {
  const [products, setProducts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const KEY = process.env.REACT_APP_ORDERS_KEY;
  const load = () => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || [])).catch(() => {});
    axios.get(`${API}/restock-waitlist`, { params: { key: KEY } }).then((r) => setWaitlist(r.data.waitlist)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const toggle = async (p) => {
    const { data } = await axios.put(`${API}/products/${p.product_id}/stock`, { sold_out: !p.sold_out }, { params: { key: KEY } });
    if (!data.sold_out && data.restock_notified > 0) toast.success(`Back in stock — ${data.restock_notified} buyer(s) emailed`);
    else toast.success(data.sold_out ? "Marked sold out" : "Back in stock");
    load();
  };
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="stock-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Bell size={18} className="text-[#39ff14]" /> Stock &amp; Restock Alerts
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Mark sold out — buyers get auto-emailed on restock.</p>
      <div className="space-y-3">
        {products.map((p) => {
          const waiting = waitlist.filter((w) => w.product_id === p.product_id).length;
          return (
            <div key={p.product_id} data-testid={`stock-row-${p.product_id}`} className="bg-[#0a0a0c] border border-[#22242a] p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="f-body text-xs text-white truncate">{p.name}</p>
                <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest mt-1">{p.sold_out ? "Sold out" : "In stock"}{waiting > 0 && ` · ${waiting} waiting`}</p>
              </div>
              <button data-testid={`stock-toggle-${p.product_id}`} onClick={() => toggle(p)}
                className={`shrink-0 f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2 font-bold transition-colors ${p.sold_out ? "bg-[#39ff14] text-black hover:bg-white" : "bg-[#22242a] text-white hover:bg-red-500/20 hover:text-red-400"}`}>
                {p.sold_out ? `Restock${waiting ? ` & Email ${waiting}` : ""}` : "Mark Sold Out"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- Payment reconciliation ---------- */
const ReconcileCard = () => {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await axios.get(`${API}/reconcile`, auth());
      setResult(data);
      if (data.healthy) toast.success("Payments match Stripe — all reconciled");
      else toast.error("Mismatch found — review below");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Reconciliation failed");
    }
    setRunning(false);
  };
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="reconcile-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <CheckCircle2 size={18} className="text-[#39ff14]" /> Payment Reconciliation
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Cross-checks Stripe against your orders — catches any payment without a matching order.</p>
      <button data-testid="reconcile-run" onClick={run} disabled={running}
        className="f-mono text-[10px] uppercase tracking-[0.14em] px-5 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors mb-4">
        {running ? "Checking Stripe…" : "Run Reconciliation"}
      </button>
      {result && (
        <div className="space-y-3" data-testid="reconcile-results">
          <div className={`px-4 py-3 border ${result.healthy ? "border-[#39ff14]/40 bg-[#39ff14]/5" : "border-red-500/40 bg-red-500/5"}`}>
            <p className={`f-mono text-xs uppercase font-bold ${result.healthy ? "text-[#39ff14]" : "text-red-400"}`}>
              {result.healthy ? "All clear" : "Needs attention"}
            </p>
            <p className="f-body text-xs text-[#8e929b] mt-1">
              {result.matched_count} paid orders matched Stripe · {result.abandoned_checkouts} abandoned checkouts (no charge)
            </p>
          </div>
          {result.stripe_paid_order_missing.map((m) => (
            <div key={m.session_id} className="bg-red-500/10 border border-red-500/40 px-4 py-3">
              <p className="f-mono text-[10px] uppercase font-bold text-red-400">Stripe paid, order missing</p>
              <p className="f-body text-xs text-[#ecebe4] mt-1">{m.detail} — {money(m.amount_cents)} · session {m.session_id.slice(-8)}</p>
            </div>
          ))}
          {result.order_paid_stripe_missing.map((m) => (
            <div key={m.session_id} className="bg-[#f5b700]/10 border border-[#f5b700]/40 px-4 py-3">
              <p className="f-mono text-[10px] uppercase font-bold text-[#f5b700]">Order paid, no Stripe session</p>
              <p className="f-body text-xs text-[#ecebe4] mt-1">{m.order_number} — {money(m.amount_cents)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Shipping settings + live rate check ---------- */
const ShippingSettingsCard = ({ onChanged }) => {
  const [pref, setPref] = useState("cheapest");
  const [mode, setMode] = useState("");
  const [rates, setRates] = useState(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    axios.get(`${API}/shipping/settings`, auth()).then((r) => { setPref(r.data.carrier_pref); setMode(r.data.mode); }).catch(() => {});
  }, []);
  const save = async (next) => {
    setPref(next);
    try {
      await axios.put(`${API}/shipping/settings`, { carrier_pref: next }, auth());
      toast.success(`Carrier preference: ${next.replace(/_/g, " ")}`);
      if (onChanged) onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const checkRates = async () => {
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/shipping/rate-check`, auth());
      setRates(data);
      toast.success(`Live ${data.mode} rates loaded`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setChecking(false);
  };
  const opts = [
    { id: "cheapest", label: "Cheapest USPS", note: "saves the most per label" },
    { id: "usps_priority", label: "Priority Mail", note: "1–3 business days" },
    { id: "usps_ground", label: "Ground Advantage", note: "2–5 business days" },
  ];
  return (
    <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="shipping-settings-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Truck size={18} className="text-[#39ff14]" /> Shipping Settings
        {mode && <span className={`f-mono text-[9px] font-bold px-2 py-1 uppercase ${mode === "production" ? "bg-[#39ff14] text-black" : "bg-[#f5b700] text-black"}`}>{mode}</span>}
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-5">Labels auto-buy on every paid order. Pick the USPS service that wins.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-[0.2em] mb-3">1 · Carrier preference</p>
          {opts.map((o) => (
            <button key={o.id} data-testid={`carrier-${o.id}`} onClick={() => save(o.id)}
              className={`w-full text-left px-4 py-3 border rounded-lg transition-colors flex items-center justify-between ${pref === o.id ? "border-[#39ff14] bg-[#39ff14]/10 d1-glow" : "border-[#22242a] bg-[#0a0a0c] hover:border-[#39ff14]/40"}`}>
              <span>
                <span className={`f-disp text-sm uppercase ${pref === o.id ? "text-[#39ff14]" : "text-white"}`}>{o.label}</span>
                <span className="f-body text-[10px] text-[#8e929b] block">{o.note}</span>
              </span>
              {pref === o.id && <CheckCircle2 size={16} className="text-[#39ff14] shrink-0" />}
            </button>
          ))}
          <button data-testid="rate-check" onClick={checkRates} disabled={checking}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#22242a] text-white font-bold hover:bg-[#39ff14] hover:text-black disabled:opacity-50 transition-colors rounded-lg mt-3">
            {checking ? "Pulling live rates…" : "Test Live Rates"}
          </button>
        </div>
        <div>
          <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-[0.2em] mb-3">2 · Live rate preview</p>
          {!rates && (
            <div className="border border-dashed border-[#22242a] rounded-lg px-4 py-8 text-center" data-testid="rate-check-empty">
              <p className="f-body text-xs text-[#8e929b]">Hit <span className="text-white font-bold">Test Live Rates</span> to preview today's USPS prices and confirm your auto-pick.</p>
            </div>
          )}
          {rates && (
            <div className="space-y-1.5" data-testid="rate-check-results">
              <p className="f-mono text-[9px] text-[#8e929b] uppercase tracking-widest">{rates.sample_route}</p>
              {rates.rates.slice(0, 6).map((r) => (
                <div key={r.service} className={`flex justify-between items-center rounded-lg border px-3 py-2 ${rates.auto_selected === r.service ? "bg-[#39ff14]/10 border-[#39ff14]/40" : "bg-[#0a0a0c] border-[#22242a]"}`}>
                  <span className="f-body text-xs text-white">{r.service}{rates.auto_selected === r.service && <span className="text-[#39ff14] f-mono text-[9px] ml-2">← auto-picked</span>}</span>
                  <span className="f-mono text-[10px] text-[#8e929b]">{money(r.cost_cents)}{r.eta ? ` · ${r.eta}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Shipments: history, reprint, buy, bulk ---------- */
const ShipmentsCard = ({ orders, reload }) => {
  const [busy, setBusy] = useState({});
  const shipments = (orders || []).filter((o) =>
    o.payment_status === "paid" && o.label_status !== "digital_delivery");
  const pendingLabels = shipments.filter((o) => ["queued_for_pre_filled_label", "label_error", "address_issue"].includes(o.label_status));
  const failedLabels = shipments.filter((o) => ["label_error", "address_issue"].includes(o.label_status));
  const awaitingShipment = shipments.filter((o) => o.label_status === "created" && o.fulfillment_status !== "shipped" && o.fulfillment_status !== "delivered");
  const shipBadge = (o) => {
    const s = o.shipment_status || (o.fulfillment_status === "delivered" ? "delivered" : null);
    if (s === "delivered") return <span className="f-mono text-[9px] font-bold px-1.5 py-0.5 uppercase bg-[#39ff14] text-black">Delivered</span>;
    if (["in_transit", "out_for_delivery"].includes(s)) return <span className="f-mono text-[9px] font-bold px-1.5 py-0.5 uppercase bg-[#f5b700]/15 text-[#f5b700] border border-[#f5b700]/40">{s.replace(/_/g, " ")}</span>;
    if (["exception", "failed_attempt", "returned"].includes(s)) return <span className="f-mono text-[9px] font-bold px-1.5 py-0.5 uppercase bg-red-500/15 text-red-400 border border-red-500/40">{s.replace(/_/g, " ")}</span>;
    if (o.label_status === "created") return <span className="f-mono text-[9px] font-bold px-1.5 py-0.5 uppercase bg-[#39ff14]/15 text-[#39ff14] border border-[#39ff14]/40">Label created</span>;
    return null;
  };
  const buy = async (o) => {
    setBusy((b) => ({ ...b, [o.order_id]: true }));
    try {
      const { data } = await axios.post(`${API}/orders/${o.order_id}/buy-label`, null, auth());
      toast.success(`Label bought — ${data.service || ""} ${data.tracking || ""}`);
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setBusy((b) => ({ ...b, [o.order_id]: false }));
  };
  const reprint = async (o) => {
    try {
      const { data } = await axios.get(`${API}/orders/${o.order_id}/label/reprint`, auth());
      if (data.label_url) { window.open(data.label_url, "_blank"); toast.success("Label opened in a new tab"); }
      else toast.error("No label found");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const buyAll = async () => {
    setBusy((b) => ({ ...b, _bulk: true }));
    try {
      const { data } = await axios.post(`${API}/orders/buy-labels-bulk`, null, auth());
      toast.success(`${data.bought} label(s) bought${data.failed ? `, ${data.failed} failed` : ""}`);
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setBusy((b) => ({ ...b, _bulk: false }));
  };
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="shipments-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <Printer size={18} className="text-[#39ff14]" /> Shipments
        {pendingLabels.length > 0 && <span className="f-mono text-[10px] font-bold px-2 py-1 bg-[#f5b700] text-black uppercase rounded">{pendingLabels.length} need labels</span>}
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Every paid physical order. Status syncs automatically every 15 minutes.</p>
      {failedLabels.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3 mb-4" data-testid="failed-label-alert">
          <p className="f-mono text-[10px] uppercase font-bold text-red-400 flex items-center gap-2">
            <AlertTriangle size={13} /> {failedLabels.length} label{failedLabels.length > 1 ? "s" : ""} failed
          </p>
          <p className="f-body text-xs text-[#ecebe4] mt-1">
            {failedLabels.map((o) => o.order_number).join(", ")} — {failedLabels[0].label_status === "address_issue" ? "address needs fixing" : "label purchase failed"}. Check your EasyShip balance or the address, then hit Buy Label.
          </p>
        </div>
      )}
      {pendingLabels.length > 1 && (
        <button data-testid="buy-labels-bulk" onClick={buyAll} disabled={busy._bulk}
          className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors mb-4">
          {busy._bulk ? "Buying…" : `Buy All ${pendingLabels.length} Pending Labels`}
        </button>
      )}
      {shipments.length === 0 && <p className="f-body text-xs text-[#8e929b]">No shipments yet.</p>}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {shipments.map((o) => (
          <div key={o.order_id} data-testid={`shipment-row-${o.order_id}`} className="bg-[#0a0a0c] border border-[#22242a] px-4 py-3">
            <div className={`flex items-center justify-between gap-3 flex-wrap ${awaitingShipment.includes(o) ? "-m-1 p-1 rounded-lg border border-[#f5b700]/30" : ""}`}>
              <div className="min-w-0">
                <p className="f-body text-xs text-white">{o.order_number} <span className="text-[#8e929b]">· {o.customer_email || "no email"}</span></p>
                <p className="f-mono text-[9px] text-[#8e929b] mt-0.5">
                  {o.label_service || "No label yet"}
                  {o.tracking_number && <> · <a href={o.tracking_url || "#"} target="_blank" rel="noopener noreferrer" className="text-[#39ff14] hover:text-white">{o.tracking_number}</a></>}
                  {o.delivery_estimate && <> · ETA {o.delivery_estimate}</>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {shipBadge(o)}
                {o.label_status === "created" && (
                  <button data-testid={`reprint-${o.order_id}`} onClick={() => reprint(o)}
                    className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 bg-[#22242a] text-white hover:bg-[#39ff14] hover:text-black transition-colors">
                    Reprint
                  </button>
                )}
                {["queued_for_pre_filled_label", "label_error"].includes(o.label_status) && (
                  <button data-testid={`buy-label-${o.order_id}`} onClick={() => buy(o)} disabled={busy[o.order_id]}
                    className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 bg-[#39ff14] text-black hover:bg-white disabled:opacity-50 transition-colors">
                    {busy[o.order_id] ? "Buying…" : "Buy Label"}
                  </button>
                )}
                {o.label_status === "address_issue" && (
                  <span className="f-mono text-[9px] uppercase font-bold px-2.5 py-1.5 bg-red-500/15 text-red-400 border border-red-500/40">Address issue</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Business settings (owner-editable) ---------- */
const BusinessSettingsCard = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/settings/business`, auth()).then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/business`, { business_name: s.business_name, support_email: s.support_email, support_phone: s.support_phone }, auth());
      toast.success("Business settings saved");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Could not save"); }
    setSaving(false);
  };
  if (!s) return <div className="bg-[#121316] border border-[#22242a] p-6" data-testid="business-settings-card"><p className="f-body text-xs text-[#8e929b]">Loading…</p></div>;
  const field = (label, key, ph, testid) => (
    <div>
      <label className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest block mb-1.5">{label}</label>
      <input data-testid={testid} value={s[key] || ""} onChange={(e) => setS({ ...s, [key]: e.target.value })} placeholder={ph}
        className="w-full bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2.5 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]" />
    </div>
  );
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="business-settings-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1"><ShoppingBag size={18} className="text-[#39ff14]" /> Business Settings</h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Your store's contact details — shown on receipts and the site.</p>
      <div className="space-y-3 mb-4">
        {field("Business name", "business_name", "The Print Plug", "set-business-name")}
        {field("Support email", "support_email", "sales@theprintplug.net", "set-support-email")}
        {field("Support phone", "support_phone", "(optional)", "set-support-phone")}
        <div>
          <label className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest block mb-1.5">Ship-from</label>
          <p className="f-body text-xs text-[#ecebe4] bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2.5">
            {s.ship_from_name || "—"} · {s.ship_from_city}, {s.ship_from_state} · carrier: <span className="text-[#39ff14]">{s.carrier_pref}</span>
          </p>
          <p className="f-mono text-[9px] text-[#8e929b] mt-1">Carrier is set in Shipping Settings above.</p>
        </div>
      </div>
      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="f-mono text-[10px] uppercase tracking-[0.14em] px-5 py-2.5 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-50 transition-colors rounded-lg">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
};

/* ---------- Internal order note input ---------- */
const OrderNoteInput = ({ orderId, onSaved }) => {
  const [val, setVal] = useState("");
  const KEY = process.env.REACT_APP_ORDERS_KEY;
  const save = async () => {
    const note = val.trim(); if (!note) return;
    try {
      await axios.put(`${API}/orders/${orderId}/note`, { note }, { params: { key: KEY } });
      toast.success("Note saved"); setVal(""); onSaved();
    } catch { toast.error("Could not save note"); }
  };
  return (
    <div className="flex gap-2">
      <input data-testid={`note-input-${orderId}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add a note (e.g. customer called…)"
        className="flex-1 bg-[#0a0a0c] border border-[#22242a] rounded-md px-3 py-1.5 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]" />
      <button data-testid={`note-save-${orderId}`} onClick={save} disabled={!val.trim()}
        className="f-mono text-[9px] uppercase font-bold px-3 py-1.5 rounded-md bg-[#39ff14] text-black hover:bg-white disabled:opacity-50 transition-colors">Save</button>
    </div>
  );
};

/* ---------- System health ---------- */
const SystemHealthCard = () => {
  const [h, setH] = useState(null);
  const load = () => axios.get(`${API}/system/health`, auth()).then((r) => setH(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const Row = ({ label, value, ok }) => (
    <div className="flex justify-between items-center bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2">
      <span className="f-body text-xs text-[#8e929b]">{label}</span>
      <span className={`f-mono text-xs font-bold ${ok === false ? "text-red-400" : "text-[#39ff14]"}`}>{value}</span>
    </div>
  );
  if (!h) return <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="system-health-card"><p className="f-body text-xs text-[#8e929b]">Checking…</p></div>;
  return (
    <div className="bg-[#121316] border border-[#22242a] rounded-xl p-6" data-testid="system-health-card">
      <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2 mb-1">
        <CheckCircle2 size={18} className={h.ok ? "text-[#39ff14]" : "text-red-400"} /> System Health
        <span className={`f-mono text-[9px] font-bold px-2 py-1 uppercase rounded ${h.ok ? "bg-[#39ff14]/15 text-[#39ff14]" : "bg-red-500/15 text-red-400"}`}>{h.ok ? "All systems go" : "Check needed"}</span>
      </h2>
      <p className="f-body text-xs text-[#8e929b] mb-4">Live status of your store's core services.</p>
      <div className="space-y-2">
        <Row label="Database" value={h.db} ok={h.ok} />
        <Row label="Payments (Stripe)" value={h.stripe_mode ? h.stripe_mode.toUpperCase() : "—"} ok={true} />
        <Row label="Labels (EasyShip)" value={h.label_mode} ok={true} />
        <Row label="Email (Resend)" value={h.email_from || "configured"} ok={true} />
        <Row label="Total orders" value={h.orders_total} ok={true} />
        <Row label="Labels created" value={h.labels_created} ok={true} />
        <Row label="Last order" value={h.last_order_number || "—"} ok={true} />
      </div>
    </div>
  );
};

/* ---------- Live chat (key-retained) ---------- */
const ChatPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [away, setAway] = useState(false);
  const bottomRef = useRef(null);
  const KEY = process.env.REACT_APP_ORDERS_KEY;
  const loadSessions = () => axios.get(`${API}/chat/sessions`, { params: { key: KEY } }).then((r) => { setSessions(r.data.sessions); setAway(r.data.away); }).catch(() => {});
  const toggleAway = async () => {
    const n = !away; setAway(n);
    await axios.put(`${API}/chat/away`, { away: n }, { params: { key: KEY } });
    toast.success(n ? "Away mode on" : "You're online");
  };
  const loadThread = (sid) => axios.get(`${API}/chat/${sid}/messages`, { params: { key: KEY } }).then((r) => setMessages(r.data.messages)).catch(() => {});
  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 5000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!active) return; loadThread(active); const t = setInterval(() => loadThread(active), 4000); return () => clearInterval(t); }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  const sendReply = async () => {
    const body = reply.trim(); if (!body || !active) return;
    setReply(""); setMessages((m) => [...m, { sender: "owner", text: body, created_at: new Date().toISOString() }]);
    await axios.post(`${API}/chat/${active}/reply`, { text: body }, { params: { key: KEY } });
    loadSessions();
  };
  const endChat = async () => {
    if (!active || !window.confirm("End this chat?")) return;
    await axios.post(`${API}/chat/${active}/end`, null, { params: { key: KEY } });
    toast.success("Chat ended"); setActive(null); setMessages([]); loadSessions();
  };
  const activeSession = sessions.find((s) => s.session_id === active);
  const totalUnread = sessions.reduce((s, x) => s + (x.owner_unread || 0), 0);
  return (
    <div className="bg-[#121316] border border-[#22242a]" data-testid="chat-panel-owner">
      <div className="px-5 py-4 border-b border-[#22242a] flex items-center justify-between">
        <h2 className="f-disp text-xl uppercase text-white flex items-center gap-2"><MessageCircle size={18} className="text-[#39ff14]" /> Live Chat</h2>
        <div className="flex items-center gap-3">
          {totalUnread > 0 && <span data-testid="chat-unread-badge" className="f-mono text-[10px] font-bold px-2 py-1 bg-[#f5b700] text-black uppercase">{totalUnread} new</span>}
          <button data-testid="chat-away-toggle" onClick={toggleAway}
            className={`f-mono text-[10px] font-bold px-3 py-1.5 uppercase tracking-wider border transition-colors ${away ? "bg-[#f5b700]/15 text-[#f5b700] border-[#f5b700]/40" : "bg-[#39ff14]/15 text-[#39ff14] border-[#39ff14]/40"}`}>
            {away ? "Away · Auto-reply on" : "Online"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 min-h-[300px]">
        {/* session list — hidden on mobile while a thread is open */}
        <div className={`${active ? "hidden md:block" : ""} border-b md:border-b-0 md:border-r border-[#22242a] max-h-[300px] overflow-y-auto`}>
          {sessions.length === 0 && <p className="f-body text-xs text-[#8e929b] p-5" data-testid="chat-no-sessions">No chats yet.</p>}
          {sessions.map((s) => (
            <button key={s.session_id} data-testid={`chat-session-${s.session_id}`} onClick={() => setActive(s.session_id)}
              className={`w-full text-left px-5 py-4 border-b border-[#22242a] transition-colors border-l-2 ${
                s.owner_unread > 0 ? "border-l-[#39ff14] bg-[#39ff14]/5"
                : active === s.session_id ? "border-l-[#39ff14]/60 bg-[#0a0a0c]"
                : "border-l-transparent hover:bg-[#0a0a0c]/50"}`}>
              <div className="flex items-center justify-between">
                <span className={`f-body text-sm ${s.owner_unread > 0 ? "text-white font-semibold" : "text-white"}`}>
                  {s.name}{s.closed && <span className="text-red-400 f-mono text-[9px] uppercase ml-2">ended</span>}
                </span>
                {s.owner_unread > 0 && (
                  <span className="f-mono text-[9px] font-bold px-1.5 py-0.5 bg-[#39ff14] text-black rounded-full d1-glow animate-pulse">
                    {s.owner_unread} new
                  </span>
                )}
              </div>
              {s.email && <p className="f-mono text-[9px] text-[#39ff14] mt-0.5">{s.email}</p>}
              <div className="flex items-center justify-between mt-1">
                <p className="f-mono text-[10px] text-[#8e929b]">{s.last_message_at ? new Date(s.last_message_at).toLocaleString("en-US") : "just started"}</p>
                {(s.orders || []).length > 0 && (
                  <span className="f-mono text-[9px] text-[#f5b700]">{s.orders.length} order{s.orders.length > 1 ? "s" : ""}</span>
                )}
              </div>
            </button>
          ))}
        </div>
        <div className={`${active ? "" : "hidden md:flex"} md:col-span-2 flex flex-col`}>
          {active && (
            <div className="px-4 py-2.5 border-b border-[#22242a]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <button data-testid="chat-back-button" onClick={() => setActive(null)}
                    className="md:hidden f-mono text-[10px] font-bold px-2 py-1.5 uppercase text-[#8e929b] hover:text-[#39ff14] transition-colors shrink-0">
                    ← Chats
                  </button>
                  <span className="f-mono text-[10px] text-[#8e929b] uppercase tracking-wider truncate">
                    {activeSession?.name}{activeSession?.email ? ` · ${activeSession.email}` : ""}{activeSession?.closed && <span className="text-red-400 ml-2">· ended</span>}
                  </span>
                </div>
                {!activeSession?.closed && (
                  <button data-testid="chat-end-button" onClick={endChat}
                    className="f-mono text-[10px] font-bold px-3 py-1.5 uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/40 rounded-md hover:bg-red-500 hover:text-white transition-colors shrink-0">End Chat</button>
                )}
              </div>
              {(activeSession?.orders || []).length > 0 && (
                <div className="mt-2 space-y-1" data-testid="chat-customer-orders">
                  {activeSession.orders.map((o) => (
                    <div key={o.order_number} className="flex justify-between items-center bg-[#0a0a0c] border border-[#22242a] rounded-md px-2.5 py-1.5">
                      <span className="f-mono text-[9px] text-white">{o.order_number}</span>
                      <span className="f-mono text-[9px] text-[#8e929b]">
                        {money(o.amount_total_cents)} · {o.payment_status}{o.shipment_status ? ` · ${o.shipment_status.replace(/_/g, " ")}` : o.fulfillment_status === "shipped" ? " · shipped" : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[240px]" data-testid="chat-thread">
            {!active && <p className="f-body text-xs text-[#8e929b] mt-8 text-center hidden md:block">Pick a chat to reply.</p>}
            {active && messages.map((m, i) => (
              <div key={i} className={`flex ${m.sender === "owner" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 text-xs f-body leading-relaxed rounded-lg ${m.sender === "owner" ? "bg-[#39ff14] text-black" : "bg-[#22242a] text-[#ecebe4]"}`}>{m.text}</div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-[#22242a] flex gap-2">
            <input data-testid="chat-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()}
              placeholder={active ? "Reply to visitor…" : "Select a chat first"} disabled={!active}
              className="flex-1 bg-[#0a0a0c] border border-[#22242a] rounded-lg px-3 py-2 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] disabled:opacity-50" />
            <button data-testid="chat-reply-send" onClick={sendReply} disabled={!active}
              className="w-10 rounded-lg bg-[#39ff14] text-black flex items-center justify-center hover:bg-white disabled:opacity-50 transition-colors" aria-label="Send reply">
              <Send size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= MAIN PAGE ================= */
export default function OrdersDashboardPage() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [shipping, setShipping] = useState({});
  const [refunding, setRefunding] = useState({});
  const KEY = process.env.REACT_APP_ORDERS_KEY;

  const load = () => axios.get(`${API}/orders`, { params: { key: KEY } }).then((r) => setData(r.data)).catch(() => {});
  const loadAnalytics = () => axios.get(`${API}/analytics`, auth()).then((r) => setAnalytics(r.data)).catch(() => {});

  useEffect(() => {
    load(); loadAnalytics();
    const t = setInterval(() => { load(); loadAnalytics(); }, 15000);
    return () => clearInterval(t);
  }, []);

  const exportReport = async () => {
    try {
      const r = await axios.get(`${API}/reports/export`, { ...auth(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "orders-report.csv"; a.click();
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
  };

  const markShipped = async (orderId) => {
    const tracking = window.prompt("USPS tracking number (optional — buyer gets emailed it):", "") ?? "";
    setShipping((s) => ({ ...s, [orderId]: true }));
    try {
      await axios.post(`${API}/orders/${orderId}/ship`, null, { ...auth(), params: { key: KEY, tracking: tracking.trim() } });
      toast.success("Marked as shipped — buyer notified");
      load(); loadAnalytics();
    } catch { toast.error("Could not update order"); }
    setShipping((s) => ({ ...s, [orderId]: false }));
  };

  const refundOrder = async (orderId, name) => {
    if (!window.confirm(`Refund ${name} in full via Stripe? Cannot be undone.`)) return;
    setRefunding((s) => ({ ...s, [orderId]: true }));
    try {
      await axios.post(`${API}/orders/${orderId}/refund`, null, { ...auth(), params: { key: KEY } });
      toast.success("Refunded via Stripe");
      load(); loadAnalytics();
    } catch (e) { toast.error("Refund failed", { description: fmtErr(e.response?.data?.detail) }); }
    setRefunding((s) => ({ ...s, [orderId]: false }));
  };

  return (
    <div className="bg-[#0a0a0c] text-[#ecebe4] min-h-screen selection:bg-[#39ff14] selection:text-black">
      <nav className="sticky top-0 z-50 bg-[#0a0a0c]/90 backdrop-blur border-b border-[#22242a] px-6 py-4 flex items-center justify-between">
        <span className="flex items-center gap-3">
          <img src="/logo.png" alt="The Print Plug logo" className="w-10 h-10 object-contain" />
          <span className="f-disp tracking-wider text-xl uppercase leading-none text-white">DISPATCH DECK<span className="text-[#39ff14]">.</span></span>
        </span>
        <div className="flex items-center gap-4">
          <button data-testid="export-report" onClick={exportReport}
            className="f-mono text-xs uppercase tracking-[0.14em] text-[#8e929b] hover:text-[#39ff14] transition-colors flex items-center gap-1.5">
            <Download size={13} /> Export
          </button>
          <Link to="/" data-testid="dashboard-store-link" className="f-mono text-xs uppercase tracking-[0.14em] text-[#8e929b] hover:text-[#39ff14] transition-colors">View Store</Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="f-mono text-xs text-[#39ff14] uppercase tracking-[0.14em] mb-2">Owner Only</p>
        <h1 className="f-disp text-[clamp(32px,4vw,56px)] leading-[0.95] tracking-[-0.03em] uppercase text-white mb-8">Command Center</h1>

        {/* Revenue hero */}
        {analytics && (
          <div className="bg-gradient-to-r from-[#121316] to-[#0f1a0c] border border-[#39ff14]/30 rounded-xl p-6 sm:p-8 mb-6 d1-glow" data-testid="revenue-hero">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-[0.2em] mb-2">Total Revenue</p>
                <p className="f-disp text-[clamp(36px,5vw,64px)] leading-none text-white tabular-nums" data-testid="hero-total-revenue">
                  {money(analytics.total_revenue_cents)}
                </p>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-1">Today</p>
                  <p className="f-disp text-2xl text-[#39ff14] tabular-nums">{money(analytics.today_sales_cents)}</p>
                  <p className="f-mono text-[9px] text-[#8e929b] uppercase mt-1">{analytics.today_orders} order{analytics.today_orders === 1 ? "" : "s"}</p>
                </div>
                <div>
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-1">All-time Orders</p>
                  <p className="f-disp text-2xl text-white tabular-nums">{analytics.paid_orders}</p>
                  <p className="f-mono text-[9px] text-[#8e929b] uppercase mt-1">paid</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="quick-actions">
          <button data-testid="qa-export" onClick={exportReport}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#121316] border border-[#22242a] text-white font-bold rounded-lg hover:border-[#39ff14] hover:text-[#39ff14] transition-colors flex items-center gap-2">
            <Download size={13} /> Export Report
          </button>
          <button data-testid="qa-announcement" onClick={() => document.querySelector("[data-testid='announcement-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#121316] border border-[#22242a] text-white font-bold rounded-lg hover:border-[#39ff14] hover:text-[#39ff14] transition-colors flex items-center gap-2">
            <Megaphone size={13} /> Post Announcement
          </button>
          <button data-testid="qa-shipping" onClick={() => document.querySelector("[data-testid='shipments-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#121316] border border-[#22242a] text-white font-bold rounded-lg hover:border-[#39ff14] hover:text-[#39ff14] transition-colors flex items-center gap-2">
            <Printer size={13} /> Shipping Center
          </button>
          <button data-testid="qa-coupon" onClick={() => document.querySelector("[data-testid='coupons-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2.5 bg-[#121316] border border-[#22242a] text-white font-bold rounded-lg hover:border-[#39ff14] hover:text-[#39ff14] transition-colors flex items-center gap-2">
            <Tag size={13} /> New Coupon
          </button>
        </div>

        {/* KPI grid */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="dashboard-stats">
            <Stat icon={Clock} label="Pending Orders" value={analytics.pending_orders} accent="text-[#f5b700]" />
            <Stat icon={CheckCircle2} label="Paid Orders" value={analytics.paid_orders} />
            <Stat icon={Truck} label="Need Shipment" value={analytics.orders_needing_shipment} accent="text-[#f5b700]" />
            <Stat icon={RotateCcw} label="Refund Requests" value={analytics.refund_requests} accent="text-red-400" />
          </div>
        )}

        {/* Pending orders strip — unpaid/abandoned checkouts surfaced clearly */}
        {data && data.orders.filter((o) => o.payment_status === "pending").length > 0 && (
          <div className="bg-[#f5b700]/5 border border-[#f5b700]/40 rounded-xl p-5 mb-8" data-testid="pending-orders-strip">
            <p className="f-mono text-[10px] text-[#f5b700] uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2">
              <Clock size={13} /> Pending — started checkout, haven't paid
            </p>
            <div className="space-y-2">
              {data.orders.filter((o) => o.payment_status === "pending").slice(0, 5).map((o) => (
                <div key={o.order_id} className="flex justify-between items-center bg-[#0a0a0c] border border-[#22242a] rounded-lg px-4 py-2.5">
                  <span className="f-mono text-xs text-white">{o.order_number}</span>
                  <span className="f-mono text-[10px] text-[#8e929b]">{o.created_at ? new Date(o.created_at).toLocaleString("en-US") : ""}</span>
                  <span className="f-mono text-xs text-[#f5b700] tabular-nums">{money(o.amount_total_cents)}</span>
                </div>
              ))}
            </div>
            <p className="f-body text-[10px] text-[#8e929b] mt-3">These expire on their own — nothing to do unless a buyer asks.</p>
          </div>
        )}

        <div className="mb-10">{analytics && <AnalyticsCard data={analytics} />}</div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <NotificationsCard />
          <SecurityCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <CustomersCard />
          <CouponsCard />
        </div>

        <div className="mb-10"><ReconcileCard /></div>

        <div className="mb-10"><ShippingSettingsCard onChanged={() => { load(); }} /></div>
        <div className="mb-10"><ShipmentsCard orders={data ? data.orders : []} reload={() => { load(); loadAnalytics(); }} /></div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <AnnouncementCard />
          <StockCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <BusinessSettingsCard />
          <SystemHealthCard />
        </div>

        <div className="mb-10"><ChatPanel /></div>

        <div className="mb-10"><ReviewsCard /></div>

        {/* Orders */}
        <h2 className="f-disp text-2xl uppercase text-white mb-5 flex items-center gap-2"><Package size={20} className="text-[#39ff14]" /> Orders</h2>
        {(!data || data.orders.length === 0) && (
          <p data-testid="dashboard-empty" className="f-body text-sm text-[#8e929b]">No orders yet.</p>
        )}
        <div className="space-y-4" data-testid="orders-list">
          {data && data.orders.map((o) => (
            <div key={o.order_id} data-testid={`order-row-${o.order_id}`} className="bg-[#121316] border border-[#22242a] p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <p className="f-mono text-xs text-[#8e929b] uppercase tracking-wider">Order</p>
                  <p className="f-disp text-lg text-white">{o.order_number || o.order_id}</p>
                  <p className="f-mono text-[10px] text-[#8e929b] mt-1">{o.created_at ? new Date(o.created_at).toLocaleString("en-US") : ""}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {o.fulfillment_status === "shipped" && (
                    <span className="f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase bg-[#39ff14]/15 text-[#39ff14] border border-[#39ff14]/40 flex items-center gap-1">
                      <CheckCircle2 size={11} strokeWidth={2.5} /> Shipped
                    </span>
                  )}
                  {o.shipment_status && ["in_transit", "out_for_delivery"].includes(o.shipment_status) && (
                    <span className="f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase bg-[#f5b700]/15 text-[#f5b700] border border-[#f5b700]/40 flex items-center gap-1">
                      <Truck size={11} strokeWidth={2.5} /> {o.shipment_status.replace(/_/g, " ")}
                    </span>
                  )}
                  {o.shipment_status === "delivered" && (
                    <span className="f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase bg-[#39ff14] text-black flex items-center gap-1">
                      <Package size={11} strokeWidth={2.5} /> Delivered
                    </span>
                  )}
                  {["exception", "failed_attempt", "returned"].includes(o.shipment_status) && (
                    <span className="f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase bg-red-500/15 text-red-400 border border-red-500/40 flex items-center gap-1">
                      <AlertTriangle size={11} strokeWidth={2.5} /> {o.shipment_status.replace(/_/g, " ")}
                    </span>
                  )}
                  <span className={`f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase flex items-center gap-1 ${o.payment_status === "paid" ? "bg-[#39ff14] text-black" : o.payment_status === "refunded" ? "bg-red-500/20 text-red-400 border border-red-500/40" : "bg-[#22242a] text-[#8e929b]"}`}>
                    {o.payment_status === "paid" ? <CheckCircle2 size={11} strokeWidth={2.5} /> : o.payment_status === "refunded" ? <RotateCcw size={11} strokeWidth={2.5} /> : <Clock size={11} strokeWidth={2.5} />}
                    {o.payment_status}
                  </span>
                  <span data-testid={`order-label-status-${o.order_id}`} className={`f-mono text-[10px] font-bold px-2 py-1 rounded-md uppercase ${labelBadge(o.label_status)}`}>{labelText(o.label_status)}</span>
                  <span className="f-disp text-xl text-white tabular-nums">{money(o.amount_total_cents)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-2">Items</p>
                  {(o.items || []).map((i) => (
                    <p key={i.product_id + (i.variant_id || "")} className="f-body text-xs text-[#ecebe4]">
                      {i.name} × {i.qty} — <span className="text-[#39ff14]">{money(i.unit_price_cents * i.qty)}</span>
                    </p>
                  ))}
                </div>
                <div>
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-2">Ship To</p>
                  {o.shipping_details?.address ? (
                    <>
                      <p className="f-body text-xs text-[#ecebe4] leading-relaxed">
                        {o.shipping_details.name}<br />
                        {o.shipping_details.address.line1}{o.shipping_details.address.line2 ? `, ${o.shipping_details.address.line2}` : ""}<br />
                        {o.shipping_details.address.city}, {o.shipping_details.address.state} {o.shipping_details.address.postal_code}<br />
                        {o.shipping_details.address.country}
                      </p>
                      <button data-testid={`copy-address-${o.order_id}`}
                        onClick={() => {
                          const a = o.shipping_details.address;
                          navigator.clipboard.writeText(`${o.shipping_details.name}\n${a.line1}${a.line2 ? ` ${a.line2}` : ""}\n${a.city}, ${a.state} ${a.postal_code}\n${a.country}`);
                          toast.success("Address copied");
                        }}
                        className="mt-3 inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2 bg-[#22242a] text-white font-bold hover:bg-[#39ff14] hover:text-black transition-colors">
                        <Copy size={12} strokeWidth={2.5} /> Copy Address
                      </button>
                    </>
                  ) : (
                    <p className="f-body text-xs text-[#8e929b]">Address appears after payment</p>
                  )}
                  {o.customer_email && <p className="f-mono text-[10px] text-[#8e929b] mt-2">{o.customer_email}</p>}
                  {o.customer_phone && <p className="f-mono text-[10px] text-[#8e929b] mt-1">{o.customer_phone}</p>}
                </div>
                <div className="min-w-0">
                  <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-2">Label &amp; Tracking</p>
                  {o.label_status === "digital_delivery" ? (
                    <p className="f-body text-xs text-[#8e929b]">Digital template — delivered instantly.</p>
                  ) : o.label_status === "created" ? (
                    <div className="space-y-2">
                      <a data-testid={`order-label-pdf-${o.order_id}`} href={o.label_url} target="_blank" rel="noopener noreferrer"
                        className="inline-block f-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors">
                        Print USPS Label PDF
                      </a>
                      {o.tracking_number && (
                        <p className="f-mono text-[10px] text-[#8e929b]">
                          Tracking: <a href={o.tracking_url || "#"} target="_blank" rel="noopener noreferrer" className="text-[#39ff14] hover:text-white transition-colors">{o.tracking_number}</a>
                        </p>
                      )}
                      {o.label_service && <p className="f-mono text-[9px] text-[#8e929b] uppercase">{o.label_service}</p>}
                    </div>
                  ) : (
                    <p className="f-body text-xs text-[#8e929b]">{o.label_status === "label_error" ? `Label error: ${o.label_error || "failed"}` : "Label will auto-generate on payment."}</p>
                  )}
                  {o.payment_status === "paid" && o.fulfillment_status !== "shipped" && o.label_status !== "digital_delivery" && (
                    <button data-testid={`mark-shipped-${o.order_id}`} onClick={() => markShipped(o.order_id)} disabled={shipping[o.order_id]}
                      className="mt-3 inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2 bg-[#39ff14] text-black font-bold hover:bg-white disabled:opacity-60 transition-colors">
                      <Truck size={12} strokeWidth={2.5} /> {shipping[o.order_id] ? "Saving…" : "Mark as Shipped"}
                    </button>
                  )}
                  {o.payment_status === "paid" && (
                    <button data-testid={`refund-${o.order_id}`} onClick={() => refundOrder(o.order_id, o.items?.[0]?.name || "this order")} disabled={refunding[o.order_id]}
                      className="mt-3 ml-2 inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2 bg-red-500/15 text-red-400 border border-red-500/40 font-bold hover:bg-red-500 hover:text-white disabled:opacity-60 transition-colors">
                      <RotateCcw size={12} strokeWidth={2.5} /> {refunding[o.order_id] ? "Refunding…" : "Refund"}
                    </button>
                  )}
                  {/* Internal notes */}
                  <div className="mt-4 pt-3 border-t border-[#22242a]">
                    <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-widest mb-2">Internal Notes</p>
                    {(o.internal_notes || []).length > 0 && (
                      <div className="space-y-1 mb-2">
                        {o.internal_notes.map((n, i) => (
                          <p key={i} className="f-body text-xs text-[#ecebe4] bg-[#0a0a0c] border border-[#22242a] rounded px-2 py-1.5 break-all">
                            {n.note} <span className="f-mono text-[8px] text-[#8e929b] ml-1 whitespace-nowrap">{n.at ? new Date(n.at).toLocaleDateString("en-US") : ""}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    <OrderNoteInput orderId={o.order_id} onSaved={() => { load(); }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
